/**
 * PojistovnaForm — insurer **create** surface (`/pujcovna/sprava/pojistovny/nova`). Create is one full page (AGENTS.md editing pattern);
 * editing is `PojistovnaEditModal`; the form body is shared. `pujcovna:admin`, like the API.
 */

import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { AppRail } from '@/components/AppRail';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Button } from '@/components/Button';
import { Crown } from '@/components/Crown';
import { DisplayHeading } from '@/components/DisplayHeading';
import { EmptyState } from '@/components/EmptyState';
import { FormPanel, SectionHeader } from '@/components/FormPanel';
import { Icon } from '@/components/Icon';
import { Savebar } from '@/components/Savebar';
import { Shell } from '@/components/Shell';
import { Skeleton } from '@/components/Skeleton';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { useModuleAccess } from '@/lib/permissions';
import { useCreatePojistovnaMutation, useTarifyQuery } from './api';
import { POJISTOVNA_FORM_ID, PojistovnaFormBody, pojistovnaSeed, pojistovnaToBody, type PojistovnaValue } from './pojistovna-section-forms';

export function PojistovnaForm() {
  const navigate = useNavigate();
  const { can, resolved } = useModuleAccess();
  const create = useCreatePojistovnaMutation();
  const saving = create.isPending;
  const denied = resolved && !can('pujcovna', 'admin');
  const tarify = useTarifyQuery(resolved && !denied);
  const [nazev, setNazev] = useState('');

  const seed = useMemo(() => (tarify.data ? pojistovnaSeed(tarify.data) : null), [tarify.data]);
  const back = () => navigate({ to: '/pujcovna/sprava', search: { nastroj: 'pojistovny' } });

  const submit = (form: PojistovnaValue) => {
    if (!form.nazev.trim()) {
      toast.error('Zadejte název pojišťovny.');
      return;
    }
    const body = pojistovnaToBody(form, tarify.data ?? []);
    if (!body.ok) {
      toast.error(body.chyba);
      return;
    }
    create.mutate(body.body, {
      onSuccess: () => {
        toast.ok('Pojišťovna vytvořena.');
        back();
      },
      onError: (err) => toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se vytvořit pojišťovnu.'}`),
    });
  };

  return (
    <Shell rail={<AppRail active="pujcovna" />}>
      <main className="flex min-w-0 flex-col overflow-auto">
        <Crown>
          <Breadcrumb
            segments={[
              { label: 'Půjčovna', href: '/pujcovna' },
              { label: 'Správa', href: '/pujcovna/sprava?nastroj=pojistovny' },
              { label: 'Nová pojišťovna' },
            ]}
          />
          <Crown.Row className="mt-3">
            <DisplayHeading>
              Nová <em>pojišťovna.</em>
            </DisplayHeading>
          </Crown.Row>
        </Crown>

        {!resolved || (!denied && !seed && !tarify.isError) ? (
          <div className="px-8 pt-5">
            <Skeleton className="h-24 w-full" />
          </div>
        ) : denied ? (
          <div data-testid="pojistovna-form-denied" className="px-8 pb-8 pt-5">
            <EmptyState eyebrow="403" title="Pojišťovny půjčovny zakládá správce půjčovny." description="Pro přístup požádejte správce firmy." />
          </div>
        ) : tarify.isError || !seed ? (
          <div className="px-8 pb-8 pt-5">
            <EmptyState eyebrow="Chyba" title="Tarify se nepodařilo načíst" description="Pojišťovna se účtuje podle tarifu vozidla — zkuste to prosím znovu za chvíli." />
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-6 px-8 py-8">
              <FormPanel>
                <SectionHeader
                  number={1}
                  title={<>Pojišťovna <em>a její sazby</em>.</>}
                  description="Pojišťovna platí denní cenu za náhradní vozidlo podle tarifu vozu; od prahu dní platí druhá cena. Sazby lze doplnit i později."
                />
                <PojistovnaFormBody
                  initial={seed}
                  tarify={tarify.data ?? []}
                  busy={saving}
                  onSubmit={submit}
                  onChange={(v) => setNazev((n) => (n === v.nazev ? n : v.nazev))}
                />
              </FormPanel>
            </div>

            <Savebar
              meta={[
                { k: 'Režim', v: 'Nový záznam' },
                { k: 'Pojišťovna', v: nazev || '—' },
              ]}
              actions={
                <>
                  <Button variant="secondary" icon={<Icon name="chevronL" size={12} />} onClick={back} disabled={saving}>
                    Zrušit
                  </Button>
                  <Button variant="ink" type="submit" form={POJISTOVNA_FORM_ID} disabled={saving}>
                    Vytvořit pojišťovnu
                  </Button>
                </>
              }
            />
          </>
        )}
      </main>
    </Shell>
  );
}
